import React, { useCallback, useEffect, useState } from 'react';
import { askLogService } from '../../services/api';
import { AskLogSummary, AskLogShape, AskLogEntry } from '../../types';
import { formatDateTime } from '../../utils/datetime';
import ScopeFilter, { ScopeSelection } from '../ScopeFilter';

// What the Ask tab is costing, and which questions are costing it.
//
// Modelled on the MCP access panel because it answers the same kind of
// question about a different surface. The useful number here is not the total
// — a total can only be stared at — but the SHAPE: the sequence of tools a
// question ran. A shape that takes eight turns because the model searched four
// ways before finding the right syntax is a prompt to fix. A shape that reads
// six conversations to answer something a count would have settled is a tool
// description to sharpen. Neither is visible in a monthly figure.
//
// Shapes are ordered by total cost rather than by frequency, because the one
// worth fixing is the one spending the money and that is not always the common
// one.

const nf = new Intl.NumberFormat();

function money(usd: number | null | undefined): React.ReactNode {
  if (usd === null || usd === undefined) {
    return <span style={{ opacity: 0.4 }} title="No rate table configured">not priced</span>;
  }
  return `$${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

function num(n: number): React.ReactNode {
  return n ? nf.format(n) : <span style={{ opacity: 0.35 }}>—</span>;
}

// A tool sequence is long and repetitive. Collapsing runs of the same tool
// ("search ×5") is what makes one shape comparable to another at a glance.
function compactShape(shape: string): string {
  const parts = shape.split(',').filter(Boolean);
  if (!parts.length) return shape;
  const runs: Array<{ name: string; n: number }> = [];
  for (const part of parts) {
    const last = runs[runs.length - 1];
    if (last && last.name === part) last.n++;
    else runs.push({ name: part, n: 1 });
  }
  return runs
    .map(r => r.name.replace(/_conversations?$/, '').replace(/_scope$/, '')
      + (r.n > 1 ? ` ×${r.n}` : ''))
    .join(' → ');
}

const AskLogPanel: React.FC = () => {
  const [scope, setScope] = useState<ScopeSelection>({});
  const [summary, setSummary] = useState<AskLogSummary | null>(null);
  const [shapes, setShapes] = useState<AskLogShape[]>([]);
  const [entries, setEntries] = useState<AskLogEntry[]>([]);
  const [canReadQuestions, setCanReadQuestions] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'' | 'console' | 'mcp'>('');
  // Shown beside Refresh. A panel that can be refreshed should say how stale
  // it is, or the button is just a thing to click hopefully.
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      territory: scope.territory,
      domain: scope.domain,
      ...(source ? { source } : {}),
    };
    try {
      const [s, sh, log] = await Promise.all([
        askLogService.summary(params),
        askLogService.shapes(params),
        askLogService.list({ ...params, limit: 50 }),
      ]);
      setSummary(s);
      setShapes(sh);
      setEntries(log.entries);
      setCanReadQuestions(log.can_read_questions);
      setLoadedAt(new Date());
    } catch {
      setError('Could not load the Ask log');
    } finally {
      setLoading(false);
    }
  }, [scope, source]);

  useEffect(() => { load(); }, [load]);

  const tile = (label: string, value: React.ReactNode, hint?: string) => (
    <div className="ask-tile" title={hint}>
      <div className="ask-tile-v">{value}</div>
      <div className="ask-tile-l">{label}</div>
    </div>
  );

  return (
    <div>
      <ScopeFilter value={scope} onChange={setScope} />

      <div className="ask-log-controls">
        <label className="small">
          Asked through{' '}
          <select value={source} onChange={e => setSource(e.target.value as any)}>
            <option value="">console and MCP</option>
            <option value="console">the console only</option>
            <option value="mcp">MCP applications only</option>
          </select>
        </label>
        <span className="ask-log-refresh">
          {loadedAt && (
            <span className="small" style={{ opacity: 0.55 }}>
              as of {loadedAt.toLocaleTimeString()}
            </span>
          )}
          <button type="button" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </span>
      </div>

      {error && <div className="error">{error}</div>}
      {loading && !summary && <div className="loading">Loading…</div>}

      {summary && (
        <>
          <div className="ask-tiles">
            {tile('questions today', num(summary.today.questions))}
            {tile('spent today', money(summary.today.cost_usd))}
            {tile('tokens today', num(summary.today.tokens),
              `Platform ceiling is ${nf.format(summary.limits.platform_daily_tokens)} per day`)}
            {tile('conversations read', num(summary.today.conversations_read),
              'Each one is a disclosure, recorded in that conversation’s own ledger')}
            {tile('asked over MCP', num(summary.today.from_mcp),
              'Questions from registered applications today, rather than from a person in this console')}
            {tile('this week', num(summary.week.questions))}
            {tile('spent this week', money(summary.week.cost_usd))}
          </div>

          {/* Only shown when non-zero. A row of zeroes trains people to ignore
              the row, and these three are the ones worth noticing. */}
          {(summary.week.capped > 0 || summary.week.errors > 0 || summary.week.ungrounded > 0) && (
            <p className="small ask-log-flags">
              This week:{' '}
              {summary.week.capped > 0 && <>{summary.week.capped} cut short by the step limit. </>}
              {summary.week.ungrounded > 0 && (
                <>{summary.week.ungrounded} cited a conversation no search returned. </>
              )}
              {summary.week.errors > 0 && <>{summary.week.errors} failed. </>}
            </p>
          )}

          <h4>Question shapes, by what they cost</h4>
          {/* No max-width here. A 72-character measure sounds sensible until
              it is applied to 12px text, where it lands at about half the page
              — cramped, and inconsistent with the retention note below, which
              has never been capped. The tables beside it are full width; the
              prose introducing them should be too. */}
          <p className="small" style={{ opacity: 0.7 }}>
            Grouped by the sequence of tools a question ran. This is where a
            prompt or a tool description is worth changing — a shape that runs
            many searches, or reads several conversations to answer something a
            count would settle, costs the same every time it recurs.
          </p>
          <div className="table-container">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Shape</th>
                  <th style={{ textAlign: 'right' }}>Asked</th>
                  <th style={{ textAlign: 'right' }}>Avg turns</th>
                  <th style={{ textAlign: 'right' }}>Avg tokens</th>
                  <th style={{ textAlign: 'right' }}>Avg time</th>
                  <th style={{ textAlign: 'right' }}>Reads</th>
                  <th style={{ textAlign: 'right' }}>Total cost</th>
                </tr>
              </thead>
              <tbody>
                {shapes.length === 0 && (
                  <tr><td colSpan={7} className="small" style={{ opacity: 0.6 }}>
                    No questions have been asked in this scope yet.
                  </td></tr>
                )}
                {shapes.map((s, i) => (
                  <tr key={i}>
                    <td className="small wrap" title={s.shape}>
                      {compactShape(s.shape)}
                      {s.capped > 0 && (
                        <span className="ask-log-warn" title="Reached the step limit">
                          {' '}· cut short {s.capped}×
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{num(s.questions)}</td>
                    <td style={{ textAlign: 'right' }}>{s.avg_turns}</td>
                    <td style={{ textAlign: 'right' }}>{num(s.avg_tokens)}</td>
                    <td style={{ textAlign: 'right' }}>{(s.avg_ms / 1000).toFixed(1)}s</td>
                    <td style={{ textAlign: 'right' }}>{num(s.conversations_read)}</td>
                    <td style={{ textAlign: 'right' }}>{money(s.total_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4>Recent questions</h4>
          {!canReadQuestions && (
            <p className="small" style={{ opacity: 0.7 }}>
              A question can name a recorded party, so only a Super User sees
              other people's. Your own are shown in full.
            </p>
          )}
          <div className="table-container">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Scope</th>
                  <th>Question</th>
                  <th style={{ textAlign: 'right' }}>Turns</th>
                  <th style={{ textAlign: 'right' }}>Read</th>
                  <th style={{ textAlign: 'right' }}>Tokens</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={9} className="small" style={{ opacity: 0.6 }}>
                    Nothing logged yet.
                  </td></tr>
                )}
                {entries.map(e => (
                  <tr key={e.id}>
                    <td className="small">{formatDateTime(e.created_at)}</td>
                    <td className="small">
                      {e.source === 'mcp' && (
                        <span
                          className="ask-src-mcp"
                          title="Asked by a registered application over MCP. An application is a processor: it cannot read a conversation whose parties have not granted the analysis purpose."
                        >MCP</span>
                      )}
                      {e.actor || e.user_email || <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td className="small monospace">
                      {e.territory === '*' ? 'all' : (e.territory || 'unassigned')}
                      {e.domain && e.domain !== '*' && ` / ${e.domain}`}
                    </td>
                    <td className="small wrap" style={{ maxWidth: '32ch' }}>
                      {e.question_withheld
                        ? <span style={{ opacity: 0.4 }}>withheld</span>
                        : e.question}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {e.turns}
                      {e.turns_capped && <span className="ask-log-warn" title="Cut short by the step limit"> ⚠</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{num(e.conversations_read)}</td>
                    <td style={{ textAlign: 'right' }} title={`${nf.format(e.thoughts_tokens)} reasoning`}>
                      {num(e.total_tokens)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{money(e.cost_usd)}</td>
                    <td className="small">
                      {e.status !== 'ok'
                        ? <span className="ask-log-warn">{e.status}</span>
                        : !e.grounded
                          ? <span className="ask-log-warn" title="Cited a conversation no search returned">ungrounded</span>
                          : <span style={{ opacity: 0.5 }}>{e.citations} cited</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="small" style={{ opacity: 0.6 }}>
            Questions are kept for {summary.retention_days} days and pruned by
            the nightly job — a stored question can name a recorded party, so it
            ages out rather than accumulating. Per-person ceiling is{' '}
            {nf.format(summary.limits.per_user_daily_tokens)} tokens a day.
          </p>
        </>
      )}
    </div>
  );
};

export default AskLogPanel;
